import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Business,
  Build,
  Schedule,
  Warning,
  CheckCircle,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { useNotification } from "../../components/common/NotificationProvider";
import { useFormValidation, commonValidationRules } from "../../hooks/useFormValidation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`facilities-tabpanel-${index}`}
      aria-labelledby={`facilities-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Facilities: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { assets, addAsset, updateAsset, deleteAsset } = useStore();
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingAsset, setEditingAsset] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    location: "",
    status: "Operational",
    lastMaintenance: "",
    nextMaintenance: "",
    value: "",
  });

  const validationRules = {
    name: commonValidationRules.name,
    category: { required: true },
    location: { required: true },
    lastMaintenance: commonValidationRules.date,
    nextMaintenance: commonValidationRules.date,
    value: commonValidationRules.amount,
  };

  const { errors, validateForm, validateFieldOnChange, clearErrors } = useFormValidation(validationRules);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: string, asset?: any) => {
    setDialogType(type);
    if (asset) {
      setEditingAsset(asset);
      setFormData({
        name: asset.name,
        category: asset.category,
        location: asset.location,
        status: asset.status,
        lastMaintenance: asset.lastMaintenance,
        nextMaintenance: asset.nextMaintenance,
        value: asset.value.toString(),
      });
    } else {
      setEditingAsset(null);
      setFormData({
        name: "",
        category: "",
        location: "",
        status: "Operational",
        lastMaintenance: "",
        nextMaintenance: "",
        value: "",
      });
    }
    clearErrors();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType("");
    setEditingAsset(null);
    setFormData({
      name: "",
      category: "",
      location: "",
      status: "Operational",
      lastMaintenance: "",
      nextMaintenance: "",
      value: "",
    });
    clearErrors();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateFieldOnChange(field, value);
  };

  const handleSubmit = () => {
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting");
      return;
    }

    try {
      const assetData = {
        name: formData.name,
        category: formData.category,
        location: formData.location,
        status: formData.status,
        lastMaintenance: formData.lastMaintenance,
        nextMaintenance: formData.nextMaintenance,
        value: parseFloat(formData.value),
      };

      if (editingAsset) {
        updateAsset(editingAsset.id, assetData);
        showSuccess("Asset updated successfully");
      } else {
        addAsset(assetData);
        showSuccess("Asset added successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showError("Failed to save asset");
    }
  };

  const handleDelete = (id: number) => {
    try {
      deleteAsset(id);
      showSuccess("Asset deleted successfully");
    } catch (error) {
      showError("Failed to delete asset");
    }
  };

  // Mock data
  const assetsData = [
    {
      id: 1,
      name: "MRI Scanner",
      category: "Medical Equipment",
      location: "Radiology Department",
      status: "Operational",
      lastMaintenance: "2024-01-10",
      nextMaintenance: "2024-04-10",
      value: 2500000,
    },
    {
      id: 2,
      name: "CT Scanner",
      category: "Medical Equipment",
      location: "Radiology Department",
      status: "Maintenance",
      lastMaintenance: "2024-01-15",
      nextMaintenance: "2024-02-15",
      value: 1800000,
    },
    {
      id: 3,
      name: "Operating Room Table",
      category: "Surgical Equipment",
      location: "OR-1",
      status: "Operational",
      lastMaintenance: "2024-01-12",
      nextMaintenance: "2024-03-12",
      value: 150000,
    },
  ];

  const maintenanceData = [
    {
      id: 1,
      asset: "MRI Scanner",
      type: "Preventive",
      scheduledDate: "2024-01-20",
      status: "Scheduled",
      technician: "Ahmed Al-Maintenance",
      priority: "High",
    },
    {
      id: 2,
      asset: "CT Scanner",
      type: "Corrective",
      scheduledDate: "2024-01-18",
      status: "In Progress",
      technician: "Mohammed Hassan",
      priority: "Urgent",
    },
    {
      id: 3,
      asset: "Operating Room Table",
      type: "Preventive",
      scheduledDate: "2024-01-25",
      status: "Completed",
      technician: "Fatima Al-Technician",
      priority: "Medium",
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Operational":
      case "Completed":
        return "success";
      case "Maintenance":
      case "In Progress":
      case "Scheduled":
        return "warning";
      case "Urgent":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("facilities.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Facilities and asset management system
      </Typography>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Business color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">1,250</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Assets
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Build color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">95%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Operational Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Schedule color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">12</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Maintenance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Warning color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">3</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Critical Issues
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Asset Management" />
            <Tab label="Maintenance" />
            <Tab label="Reports" />
          </Tabs>
        </Box>

        {/* Asset Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Asset Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("asset")}
            >
              Add Asset
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Asset Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Maintenance</TableCell>
                  <TableCell>Next Maintenance</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>{asset.category}</TableCell>
                    <TableCell>{asset.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={asset.status}
                        color={getStatusColor(asset.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{asset.lastMaintenance}</TableCell>
                    <TableCell>{asset.nextMaintenance}</TableCell>
                    <TableCell align="right">
                      SAR {asset.value.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog("asset", asset)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(asset.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Maintenance Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Maintenance Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("maintenance")}
            >
              Schedule Maintenance
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Scheduled Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Technician</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {maintenanceData.map((maintenance) => (
                  <TableRow key={maintenance.id}>
                    <TableCell>{maintenance.asset}</TableCell>
                    <TableCell>{maintenance.type}</TableCell>
                    <TableCell>{maintenance.scheduledDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={maintenance.status}
                        color={getStatusColor(maintenance.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{maintenance.technician}</TableCell>
                    <TableCell>
                      <Chip
                        label={maintenance.priority}
                        color={getStatusColor(maintenance.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Facilities Reports
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Asset Depreciation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Asset depreciation and valuation reports
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Maintenance Schedule
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Preventive and corrective maintenance schedules
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cost Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Maintenance costs and budget analysis
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Dialog for adding new items */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "asset" && (editingAsset ? "Edit Asset" : "Add New Asset")}
          {dialogType === "maintenance" && "Schedule Maintenance"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === "asset" ? (
              <>
                <TextField
                  fullWidth
                  label="Asset Name"
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Category</InputLabel>
                  <Select 
                    label="Category"
                    value={formData.category}
                    onChange={(e) => handleInputChange("category", e.target.value)}
                  >
                    <MenuItem value="Medical Equipment">Medical Equipment</MenuItem>
                    <MenuItem value="Surgical Equipment">Surgical Equipment</MenuItem>
                    <MenuItem value="Furniture">Furniture</MenuItem>
                    <MenuItem value="IT Equipment">IT Equipment</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Location"
                  variant="outlined"
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                  error={!!errors.location}
                  helperText={errors.location}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select 
                    label="Status"
                    value={formData.status}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                  >
                    <MenuItem value="Operational">Operational</MenuItem>
                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                    <MenuItem value="Out of Service">Out of Service</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Last Maintenance"
                  type="date"
                  variant="outlined"
                  value={formData.lastMaintenance}
                  onChange={(e) => handleInputChange("lastMaintenance", e.target.value)}
                  error={!!errors.lastMaintenance}
                  helperText={errors.lastMaintenance}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Next Maintenance"
                  type="date"
                  variant="outlined"
                  value={formData.nextMaintenance}
                  onChange={(e) => handleInputChange("nextMaintenance", e.target.value)}
                  error={!!errors.nextMaintenance}
                  helperText={errors.nextMaintenance}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Value (SAR)"
                  type="number"
                  variant="outlined"
                  value={formData.value}
                  onChange={(e) => handleInputChange("value", e.target.value)}
                  error={!!errors.value}
                  helperText={errors.value}
                  sx={{ mb: 2 }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Asset Name"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Category</InputLabel>
                  <Select label="Category">
                    <MenuItem value="medical">Medical Equipment</MenuItem>
                    <MenuItem value="surgical">Surgical Equipment</MenuItem>
                    <MenuItem value="furniture">Furniture</MenuItem>
                    <MenuItem value="it">IT Equipment</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={dialogType === "asset" ? handleSubmit : handleCloseDialog}>
            {dialogType === "asset" ? (editingAsset ? "Update" : "Add") : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Facilities;
